import { useState, useEffect } from 'react';

interface NotificationCompatibility {
  isSupported: boolean;
  isIOS: boolean;
  isSafari: boolean;
  isChrome: boolean;
  isFirefox: boolean;
  permission: NotificationPermission | 'default';
  canRequestPermission: boolean;
}

export const useNotificationCompatibility = (): NotificationCompatibility => {
  const [compatibility, setCompatibility] = useState<NotificationCompatibility>({
    isSupported: false,
    isIOS: false,
    isSafari: false,
    isChrome: false,
    isFirefox: false,
    permission: 'default',
    canRequestPermission: false
  });

  useEffect(() => {
    const checkCompatibility = () => {
      // Detectar navegador y plataforma
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) || 
                   (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
      const isChrome = /Chrome/.test(userAgent) && !/Edge/.test(userAgent);
      const isFirefox = /Firefox/.test(userAgent);
      
      // Verificar soporte de notificaciones
      const isSupported = 'Notification' in window && 
                         'serviceWorker' in navigator &&
                         'PushManager' in window;
      
      // Obtener permisos actuales
      const permission = isSupported ? Notification.permission : 'default';
      
      // En iOS, las notificaciones push tienen limitaciones
      const canRequestPermission = isSupported && 
                                 (permission === 'default' || permission === 'denied') &&
                                 (!isIOS || (isSafari && isIOS)); // Solo Safari en iOS soporta notificaciones push
      
      setCompatibility({
        isSupported,
        isIOS,
        isSafari,
        isChrome,
        isFirefox,
        permission,
        canRequestPermission
      });
      
      console.log('🔍 Compatibilidad de notificaciones:', {
        isSupported,
        isIOS,
        isSafari,
        isChrome,
        isFirefox,
        permission,
        canRequestPermission
      });
    };

    checkCompatibility();
    
    // Escuchar cambios en permisos
    if ('Notification' in window) {
      const handlePermissionChange = () => {
        setCompatibility(prev => ({
          ...prev,
          permission: Notification.permission,
          canRequestPermission: Notification.permission === 'default' || Notification.permission === 'denied'
        }));
      };
      
      // En algunos navegadores, el evento 'permissionchange' no está disponible
      // Por eso usamos un intervalo para verificar cambios
      const interval = setInterval(() => {
        if (Notification.permission !== compatibility.permission) {
          handlePermissionChange();
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [compatibility.permission]);

  return compatibility;
};
